import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { X, Sparkles } from "lucide-react";
import { useAnnouncements } from "@/lib/announcements";
import { useLang } from "@/lib/i18n";
import { ChangelogPanel } from "@/components/announcement/ChangelogPanel";

/**
 * 顶部站点公告 —— 右下角自动弹出的「新功能」Toast（snackbar 范式）。
 * - 进站 1.2s 自动滑入最新一条公告；「查看更新」打开完整更新日志面板；「知道了 / ✕」关闭。
 * - 关闭后记 localStorage（按公告 id），新公告才会再次出现。
 * - 右边缘与顶栏对齐：复用 Header 的栅格容器 max-w-[min(1760px,95vw)] + px-6 右对齐。
 * - 显示期间给 <body> 加 `ann-toast-open`，Chat 悬浮球（.chatbot-dock）上移避让，关闭后归位。
 * 数据当前来自 mock 数据层，后续接后端公开读接口（见 announcements.ts 顶注）。
 */
const STORAGE_KEY = "oran_toast_read_id";
const BODY_FLAG = "ann-toast-open";

type Phase = "hidden" | "entering" | "visible" | "exiting";

export function AnnouncementToast() {
  const { lang } = useLang();
  const { latest: ann } = useAnnouncements();

  const alreadyRead = useCallback(() => {
    if (!ann) return true;
    try {
      return localStorage.getItem(STORAGE_KEY) === ann.id;
    } catch {
      return false;
    }
  }, [ann]);

  const [phase, setPhase] = useState<Phase>("hidden");
  const [panelOpen, setPanelOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastRef = useRef<HTMLDivElement>(null);

  const markRead = useCallback(() => {
    if (!ann) return;
    try {
      localStorage.setItem(STORAGE_KEY, ann.id);
    } catch {
      /* ignore */
    }
  }, [ann]);

  const dismiss = useCallback(() => {
    setPhase("exiting");
    closeTimerRef.current = setTimeout(() => setPhase("hidden"), 400);
  }, []);

  // Auto-show ~1.2s after the announcement loads (unless already read)
  useEffect(() => {
    if (!ann || alreadyRead()) return;
    const t = setTimeout(() => {
      setPhase("entering");
      closeTimerRef.current = setTimeout(() => setPhase("visible"), 20);
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ann?.id]);

  // While the toast occupies the corner, dock the Chat FAB up (decoupled via body flag).
  // Publish the toast's real height so the Chat FAB docks exactly above it (any content length).
  useLayoutEffect(() => {
    const active = phase === "entering" || phase === "visible";
    if (active) {
      const h = toastRef.current?.offsetHeight;
      if (h) document.documentElement.style.setProperty("--ann-toast-h", `${h}px`);
    }
    document.body.classList.toggle(BODY_FLAG, active);
    return () => document.body.classList.remove(BODY_FLAG);
  }, [phase, lang]);

  // Esc closes the toast (when the panel isn't open)
  useEffect(() => {
    if (phase !== "visible" && phase !== "entering") return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !panelOpen) dismiss();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [phase, panelOpen, dismiss]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  if (!ann) return null;

  const isEntering = phase === "entering";
  const isExiting = phase === "exiting";
  const translateY = isEntering ? "translateY(20px)" : isExiting ? "translateY(24px)" : "translateY(0)";
  const opacity = isEntering || isExiting ? 0 : 1;

  return (
    <div className="landing-v2">
      {/* Full-width fixed rail aligned to the nav grid; only the card is interactive.
          Rail unmounts when hidden, but ChangelogPanel below stays mounted so
          "查看更新" can dismiss the toast and still open the panel. */}
      {phase !== "hidden" && (
      <div
        style={{ position: "fixed", left: 0, right: 0, bottom: "1.5rem", zIndex: 9000, pointerEvents: "none" }}
      >
        <div className="mx-auto flex max-w-[min(1760px,95vw)] justify-end px-6">
          <div
            ref={toastRef}
            role="status"
            aria-live="polite"
            aria-label={lang === "zh" ? "新功能通知" : "New feature notification"}
            className="w-full max-w-[340px]"
            style={{
              pointerEvents: "auto",
              transform: translateY,
              opacity,
              transition: isEntering
                ? "transform 480ms cubic-bezier(0.22,1,0.36,1), opacity 380ms ease"
                : isExiting
                ? "transform 360ms cubic-bezier(0.55,0,1,0.45), opacity 300ms ease"
                : "none",
              willChange: "transform, opacity",
            }}
          >
            <div
              className="glass-strong relative overflow-hidden rounded-2xl"
              style={{
                boxShadow:
                  "0 24px 64px -16px rgba(0,0,0,0.72), 0 0 0 1px rgba(255,255,255,0.10), 0 0 40px -12px hsl(var(--brand)/0.25)",
              }}
            >
              {/* brand glow strip */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  top: 0,
                  height: "2px",
                  background: "linear-gradient(90deg, transparent 0%, hsl(var(--brand)/0.8) 50%, transparent 100%)",
                  borderRadius: "inherit",
                  pointerEvents: "none",
                }}
              />

              <div className="relative flex flex-col gap-2.5 px-4 pb-3 pt-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
                      <span
                        className="absolute inline-flex h-full w-full rounded-full opacity-75"
                        style={{ background: "hsl(var(--brand))", animation: "ann-toast-ping 1.4s cubic-bezier(0,0,0.2,1) infinite" }}
                      />
                      <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: "hsl(var(--brand))" }} />
                    </span>
                    <span
                      className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em]"
                      style={{ color: "hsl(var(--brand))" }}
                    >
                      <Sparkles className="h-3 w-3" aria-hidden="true" />
                      {lang === "zh" ? "新功能" : "New"}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      markRead();
                      dismiss();
                    }}
                    aria-label={lang === "zh" ? "关闭通知" : "Close notification"}
                    className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    style={{ outlineColor: "hsl(var(--brand))" }}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>

                <h3 className="pr-2 text-[13px] font-semibold leading-snug text-foreground">{ann.title[lang]}</h3>
                <p className="line-clamp-2 text-[12px] leading-relaxed text-muted-foreground">{ann.summary[lang]}</p>

                <div className="flex items-center gap-2 pt-0.5">
                  <button
                    type="button"
                    onClick={() => {
                      markRead();
                      dismiss();
                      setTimeout(() => setPanelOpen(true), 60);
                    }}
                    className="flex-1 rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    style={{
                      background: "linear-gradient(135deg, hsl(var(--brand)/0.18), hsl(var(--brand)/0.08))",
                      border: "1px solid hsl(var(--brand)/0.45)",
                      color: "hsl(var(--brand))",
                      outlineColor: "hsl(var(--brand))",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "linear-gradient(135deg, hsl(var(--brand)/0.28), hsl(var(--brand)/0.14))";
                      e.currentTarget.style.boxShadow = "0 0 24px -6px hsl(var(--brand)/0.45)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "linear-gradient(135deg, hsl(var(--brand)/0.18), hsl(var(--brand)/0.08))";
                      e.currentTarget.style.boxShadow = "";
                    }}
                  >
                    {lang === "zh" ? "查看更新" : "What's new"}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      markRead();
                      dismiss();
                    }}
                    className="rounded-lg px-3 py-1.5 text-[12px] text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    style={{ outlineColor: "hsl(var(--brand))" }}
                  >
                    {lang === "zh" ? "知道了" : "Got it"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      <ChangelogPanel open={panelOpen} onClose={() => setPanelOpen(false)} />

      <style>{`@keyframes ann-toast-ping { 75%, 100% { transform: scale(1.8); opacity: 0; } }`}</style>
    </div>
  );
}
